import React from 'react'
import { connect } from 'react-redux'
import { collapseLeftToolbar } from '../../../../../action/left-toolbar-state-actions'
import createAreaSelector from './area-selector'
import { selectWithin, countsWithin } from './export-geometry'
import { applyHighlight, clearHighlight } from './export-highlight'
import KML from '../../../../../lib/api/kml'

class Export extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      // 'area' | 'all'
      mode: 'area',
      bounds: null,
      exportMarkers: true,
      exportPhotos: true,
      exportTracks: true,
      exportShapes: true,
      loading: false
    }
    this.selector = null
    this.setBounds = this.setBounds.bind(this)
    this.exportKML = this.exportKML.bind(this)
  }

  componentDidMount() {
    if (this.state.mode === 'area') {
      this.openSelector()
    }
  }

  componentDidUpdate() {
    this.syncHighlight()
  }

  componentWillUnmount() {
    this.closeSelector()
  }

  /** Keep the map's dimming in step with whatever the panel is reporting. */
  syncHighlight() {
    const { mode, bounds } = this.state
    if (mode !== 'area' || !bounds) {
      clearHighlight(this.props)
      return
    }
    applyHighlight(bounds, this.props, this.getSelection(), this.enabledTypes())
  }

  enabledTypes() {
    const { exportMarkers, exportPhotos, exportTracks, exportShapes } = this.state
    return {
      markers: exportMarkers,
      photos: exportPhotos,
      tracks: exportTracks,
      shapes: exportShapes
    }
  }

  openSelector() {
    if (this.selector) {
      return
    }
    // The right toolbar deliberately stays visible: the map is interactive
    // around the rectangle now, so there is nothing to move out of the way,
    // and unmounting it is what wiped the map's listeners in WEB-7076.
    this.selector = createAreaSelector({
      map: this.props.mapReference,
      onChange: this.setBounds
    })
    if (!this.selector) {
      console.warn('Cannot start an area export: the map is not ready yet')
    }
  }

  closeSelector() {
    if (this.selector) {
      this.selector.destroy()
      this.selector = null
    }
    // Leaving objects dimmed after the panel closes would look like a bug.
    clearHighlight(this.props)
  }

  setBounds(bounds) {
    this.setState({ bounds })
  }

  setMode(mode) {
    if (mode === this.state.mode) {
      return
    }
    this.setState({ mode }, () => {
      if (mode === 'area') {
        this.openSelector()
      } else {
        this.closeSelector()
        this.setState({ bounds: null })
      }
    })
  }

  toggleType(key) {
    this.setState({ [key]: !this.state[key] })
  }

  /** Everything the current settings would export. */
  getSelection() {
    const { mode, bounds } = this.state
    const { markers, photos, tracks, lines, polygons, circles } = this.props

    if (mode === 'all') {
      const measurements = [].concat(tracks, lines, polygons, circles)
      return {
        markers,
        photos,
        measurements,
        total: markers.length + photos.length + measurements.length
      }
    }

    return selectWithin(bounds, this.props, this.enabledTypes())
  }

  exportKML() {
    const selection = this.getSelection()
    if (!selection.total) {
      return
    }

    this.setState({ loading: true })
    const markerIds = selection.markers.map(marker => marker.id)
    const measurementIds = selection.measurements.map(measure => measure.id)
    const photoIds = selection.photos.map(photo => photo.id)

    KML.export(markerIds, measurementIds, photoIds, this.props.token)
      .then(res => {
        this.setState({ loading: false })
        const downloadLink = document.createElement('a')
        downloadLink.href = URL.createObjectURL(
          new Blob([res], { type: 'application/octet-stream;base64;' })
        )
        downloadLink.download = 'BaseMap.kml'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
        this.props.collapseLeftToolbar()
      })
      .catch(err => {
        this.setState({ loading: false })
        console.error('Exporting KML failed:', err)
      })
  }

  typeRow(key, label, counts, countKey) {
    const id = `exp-${countKey}-check`
    return (
      <label className='exp-type' htmlFor={id}>
        <input type='checkbox'
          id={id}
          checked={this.state[key]}
          onChange={() => this.toggleType(key)}></input>
        <span className='exp-type-name'>{label}</span>
        <span className='exp-type-count'>{counts[countKey]}</span>
      </label>
    )
  }

  areaInfo(counts) {
    const enabled = this.enabledTypes()
    const noneChecked = !enabled.markers && !enabled.photos &&
      !enabled.tracks && !enabled.shapes
    return (
      <div>
        <div className='exp-types'>
          {this.typeRow('exportMarkers', 'Export Markers', counts, 'markers')}
          {this.typeRow('exportPhotos', 'Export Photos', counts, 'photos')}
          {this.typeRow('exportTracks', 'Export Tracks', counts, 'tracks')}
          {this.typeRow('exportShapes', 'Export Shapes', counts, 'shapes')}
        </div>
        <p className='exp-rule'>
          Drag the box on the map to move it, or pull a handle to resize.
          A track or shape crossing the edge exports in full.
        </p>
        {noneChecked ? (
          <p className='exp-rule'>Nothing selected — tick at least one object type.</p>
        ) : null}
      </div>
    )
  }

  allInfo() {
    return (
      <div className='exp-rule'>
        <p>Exports every object you have created in BaseMap — all markers, tracks and
          shapes. This increases file size and can take much longer to download.</p>
      </div>
    )
  }

  render() {
    const { mode, loading } = this.state
    const selection = this.getSelection()
    const counts = mode === 'all'
      ? {
        markers: this.props.markers.length,
        photos: this.props.photos.length,
        tracks: this.props.tracks.length,
        shapes: this.props.lines.length + this.props.polygons.length + this.props.circles.length
      }
      : countsWithin(this.state.bounds, this.props)

    const cancelStyles = { width: '30%', color: 'white', backgroundColor: '#4f4f4f' }
    const uploadStyles = {
      width: '60%',
      color: 'white',
      backgroundColor: selection.total > 0 ? '#589500' : '#c1c0c0'
    }

    return (
      <div className='imp-exp-content'>
        <div className='imp-exp-title'>Export a KML File</div>
        <div className='imp-exp-details'>
          <p>
            Export map data in .KML format. This may take a few minutes depending on the file
            size and the speed of your internet connection.
          </p>
        </div>
        <div>
          <p className='imp-exp-section-header'>EXPORT OBJECTS</p>
          <div className='imp-exp-inputs'>
            <div>
              <input type='radio'
                value='area'
                name='exp-scope'
                id='exp-area'
                onChange={() => { this.setMode('area') }}
                checked={mode === 'area'}></input>
              <label htmlFor='exp-area'>By Specified Area</label>
            </div>
            <div style={{ marginLeft: '20px' }}>
              <input type='radio'
                value='all'
                name='exp-scope'
                id='exp-all'
                onChange={() => { this.setMode('all') }}
                checked={mode === 'all'}></input>
              <label htmlFor='exp-all'>All Objects</label>
            </div>
          </div>
        </div>

        <div className='imp-exp-count'>
          <div className='exp-count-total'>{selection.total}</div>
          <div className='exp-count-caption'>
            {mode === 'all' ? 'Total objects for export' : 'Total objects in the box'}
          </div>
          {mode === 'area' ? this.areaInfo(counts) : null}
        </div>

        {mode === 'all' ? this.allInfo() : null}

        <div className='imp-exp-inputs'>
          <button style={cancelStyles} onClick={this.props.collapseLeftToolbar}>CANCEL</button>
          <button style={uploadStyles}
            disabled={selection.total === 0}
            onClick={this.exportKML}>{`EXPORT ${selection.total}`}</button>
        </div>
        {loading ? (
          <div className='dialog-loading-overlay'>
          </div>
        ) : null}
      </div >
    )
  }
}

const mapStateToProps = state => ({
  mapReference: state.mapReference(),
  token: state.auth.token,
  lines: state.lines,
  markers: state.markers,
  photos: state.photos,
  circles: state.circles,
  tracks: state.tracks,
  polygons: state.polygons
})

const mapDispatchToProps = dispatch => ({
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar())
})

export default connect(mapStateToProps, mapDispatchToProps)(Export)
